import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { 
  Linkedin, 
  Users, 
  Calendar, 
  TrendingUp, 
  CheckCircle, 
  ArrowRight, 
  MessageSquare,
  Target,
  Zap,
  Shield,
  Clock,
  BarChart3,
  Send,
  Building2,
  Mail,
  Phone,
  User
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { z } from "zod";

// Form validation schema
const contactSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100, "Name must be less than 100 characters"),
  email: z.string().trim().email("Invalid email address").max(255, "Email must be less than 255 characters"),
  company: z.string().trim().min(1, "Company name is required").max(100, "Company must be less than 100 characters"),
  phone: z.string().trim().max(20, "Phone must be less than 20 characters").optional(),
  message: z.string().trim().max(1000, "Message must be less than 1000 characters").optional(),
});

// Real campaign stats - matches home hero
const campaignStats = {
  totalLeads: 13597,
  avgAcceptance: 56,
  qualifiedLeads: 487,
  bookedCalls: 107,
  campaigns: [
    { name: "Harvard | HR | Consultant | 3-5 Years", leads: 2390, acceptance: 50, response: 23.1 },
    { name: "HR Connection Request | Groups | UK | US", leads: 2418, acceptance: 49.8, response: 17.5 },
    { name: "Oxford | Consults | VIPs | No Connection", leads: 1697, acceptance: 55.7, response: 20.3 },
    { name: "Cambridge | London | VIPs | Posted", leads: 291, acceptance: 68.4, response: 22.4 },
  ],
};

// Pricing tiers
const pricingTiers = [
  {
    name: "Starter",
    price: "£1,497",
    period: "/month",
    description: "Perfect for testing LinkedIn outreach",
    features: [
      "500 connection requests/month",
      "Personalized messaging sequences",
      "Weekly performance reports",
      "1 LinkedIn profile managed",
      "Basic targeting criteria",
      "Email support",
    ],
    cta: "Get Started",
    popular: false,
  },
  {
    name: "Growth",
    price: "£2,997",
    period: "/month",
    description: "For businesses ready to scale",
    features: [
      "1,500 connection requests/month",
      "Advanced messaging sequences",
      "Daily performance dashboard",
      "2 LinkedIn profiles managed",
      "Advanced targeting & ABM",
      "Dedicated account manager",
      "CRM integration",
      "A/B testing",
    ],
    cta: "Most Popular",
    popular: true,
  },
  {
    name: "Enterprise",
    price: "Custom",
    period: "",
    description: "Full-scale LinkedIn domination",
    features: [
      "Unlimited connection requests",
      "Custom messaging strategy",
      "Real-time analytics",
      "Unlimited profiles managed",
      "Premium targeting & intent data",
      "Dedicated success team",
      "Full tech stack integration",
      "Weekly strategy calls",
      "Content creation support",
    ],
    cta: "Contact Sales",
    popular: false,
  },
];

const LinkedInSDR = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Animated counters for live growth effect - matches home hero
  const [animatedLeads, setAnimatedLeads] = useState(campaignStats.totalLeads);
  const [animatedQualified, setAnimatedQualified] = useState(campaignStats.qualifiedLeads);
  const [animatedCalls, setAnimatedCalls] = useState(campaignStats.bookedCalls);

  useEffect(() => {
    // Leads scraped: +1-23 (odd numbers) every 20 seconds
    const leadsInterval = setInterval(() => {
      const randomOdd = Math.floor(Math.random() * 12) * 2 + 1;
      setAnimatedLeads(prev => prev + randomOdd);
    }, 20000);

    // Qualified leads: +1,3,5 (odd) every 21 seconds
    const qualifiedInterval = setInterval(() => {
      const randomOdd = Math.floor(Math.random() * 3) * 2 + 1;
      setAnimatedQualified(prev => prev + randomOdd);
    }, 21000);

    // Booked calls: +1 or 3 (odd) every 86 seconds
    const callsInterval = setInterval(() => {
      const randomOdd = Math.random() > 0.7 ? 3 : 1;
      setAnimatedCalls(prev => prev + randomOdd);
    }, 86000);

    return () => {
      clearInterval(leadsInterval);
      clearInterval(qualifiedInterval);
      clearInterval(callsInterval);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    
    // Validate form data
    const result = contactSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach(err => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as string] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setIsSubmitting(true);
    
    try {
      const { error } = await supabase.from('form_submissions').insert({
        form_type: 'linkedin_sdr_inquiry',
        name: formData.name,
        email: formData.email,
        company_name: formData.company,
        phone: formData.phone || null,
        additional_data: { 
          message: formData.message,
          source: 'linkedin-sdr-page'
        },
        user_agent: navigator.userAgent,
        referrer: document.referrer || null,
      });

      if (error) throw error;
      
      toast.success("Thank you! We'll be in touch within 24 hours.");
      setFormData({ name: "", email: "", company: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>LinkedIn SDR Service | B2B Lead Generation | Vincent Growth Partners</title>
        <meta name="description" content="Generate qualified B2B leads with our LinkedIn SDR service. 56% acceptance rates, 40+ meetings booked. Transform your LinkedIn into a lead generation machine." />
      </Helmet>

      <Header />

      <main>
        {/* Hero Section */}
        <section className="relative py-20 lg:py-32 overflow-hidden bg-gradient-to-br from-[#0A66C2] via-[#004182] to-primary">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 -left-4 w-96 h-96 bg-white rounded-full mix-blend-multiply filter blur-3xl animate-pulse"></div>
            <div className="absolute bottom-0 -right-4 w-96 h-96 bg-secondary rounded-full mix-blend-multiply filter blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-6 bg-white/20 text-white border-white/30 text-sm px-4 py-2">
                <Linkedin className="w-4 h-4 mr-2 inline" />
                LinkedIn SDR Service
              </Badge>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Turn LinkedIn Into Your <span className="text-secondary">Lead Generation Machine</span>
              </h1>
              
              <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed">
                We've generated <span className="font-bold text-secondary">13,597+ leads</span> and booked <span className="font-bold text-secondary">107+ calls</span> for clients. Your competitors are already using LinkedIn SDR. Are you?
              </p>

              {/* Key Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <p className="text-3xl md:text-4xl font-bold text-white transition-all duration-300">{animatedLeads.toLocaleString()}</p>
                  <p className="text-white/70 text-sm">Leads Generated</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <p className="text-3xl md:text-4xl font-bold text-green-400">{campaignStats.avgAcceptance}%</p>
                  <p className="text-white/70 text-sm">Avg. Acceptance</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <p className="text-3xl md:text-4xl font-bold text-secondary transition-all duration-300">{animatedQualified}</p>
                  <p className="text-white/70 text-sm">Qualified Leads</p>
                </div>
                <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/30 rounded-xl p-4 border-2 border-green-400/40 relative overflow-hidden">
                  <div className="absolute inset-0 bg-green-400/10 animate-pulse"></div>
                  <div className="relative z-10">
                    <p className="text-3xl md:text-4xl font-black text-green-400 transition-all duration-300">{animatedCalls}+</p>
                    <p className="text-white text-sm font-bold">Calls Booked</p>
                    <span className="text-[10px] text-green-300 font-semibold animate-pulse">& counting</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="bg-secondary hover:bg-secondary/90 text-primary font-bold text-lg px-8">
                  <a href="#contact">
                    Schedule Strategic Review
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                How Our LinkedIn SDR Service Works
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                A proven 4-step process that transforms your LinkedIn into a predictable lead generation channel
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  icon: Target,
                  title: "1. Target Definition",
                  description: "We identify your ideal customer profile and build laser-targeted prospect lists using advanced LinkedIn Sales Navigator filters.",
                },
                {
                  icon: MessageSquare,
                  title: "2. Message Crafting",
                  description: "Our copywriters create personalized connection requests and follow-up sequences that get responses.",
                },
                {
                  icon: Zap,
                  title: "3. Automated Outreach",
                  description: "We execute campaigns at scale while maintaining a human touch. 500-1,500+ connections per month.",
                },
                {
                  icon: Calendar,
                  title: "4. Meeting Booking",
                  description: "Warm leads are nurtured and qualified. We book meetings directly into your calendar.",
                },
              ].map((step, idx) => (
                <Card key={idx} className="border-border/50 hover:border-[#0A66C2]/50 transition-colors group">
                  <CardHeader>
                    <div className="w-14 h-14 bg-[#0A66C2]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#0A66C2]/20 transition-colors">
                      <step.icon className="w-7 h-7 text-[#0A66C2]" />
                    </div>
                    <CardTitle className="text-xl">{step.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{step.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Real Campaign Results */}
        <section className="py-20 bg-[#0D1421]">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge className="mb-4 bg-[#0A66C2]/20 text-[#0A66C2] border-[#0A66C2]/30">
                Real Results
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Live Campaign Performance
              </h2>
              <p className="text-lg text-white/70 max-w-2xl mx-auto">
                These are actual campaigns we're running right now. No fluff, just results.
              </p>
            </div>

            <div className="max-w-4xl mx-auto space-y-4">
              {campaignStats.campaigns.map((campaign, idx) => {
                // Calculate qualified leads and booked meetings (15% of qualified)
                const qualified = Math.round(campaign.leads * (campaign.acceptance / 100) * (campaign.response / 100));
                const bookedMeetings = Math.round(qualified * 0.15);
                
                return (
                  <div key={idx} className="bg-white/5 rounded-xl p-6 border border-white/10 hover:border-[#0A66C2]/30 transition-colors">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <h3 className="text-white font-semibold text-lg mb-1">{campaign.name}</h3>
                        <p className="text-white/50 text-sm">{campaign.leads.toLocaleString()} leads in list</p>
                      </div>
                      <div className="flex items-center gap-4 md:gap-6">
                        <div className="text-center">
                          <p className="text-xl md:text-2xl font-bold text-green-400">{campaign.acceptance}%</p>
                          <p className="text-white/50 text-xs">Acceptance</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xl md:text-2xl font-bold text-secondary">{campaign.response}%</p>
                          <p className="text-white/50 text-xs">Response</p>
                        </div>
                        <div className="text-center bg-green-500/20 rounded-lg px-3 py-2 border border-green-400/30">
                          <p className="text-xl md:text-2xl font-black text-green-400">{bookedMeetings}</p>
                          <p className="text-white text-xs font-medium">Meetings</p>
                        </div>
                        <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse" title="Active Campaign"></div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-12 text-center">
              <p className="text-white/70 text-lg mb-6">
                Imagine these results for <span className="text-secondary font-semibold">your business</span>.
              </p>
              <Button asChild size="lg" className="bg-[#0A66C2] hover:bg-[#0A66C2]/80">
                <a href="#contact">
                  Build Your Pipeline
                  <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Why LinkedIn SDR */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <Badge className="mb-4">Why LinkedIn SDR?</Badge>
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                  Your Competitors Are Already Doing This
                </h2>
                <p className="text-lg text-muted-foreground mb-8">
                  While you're waiting, your competitors are booking meetings with your ideal customers. LinkedIn is the most powerful B2B platform with 900+ million professionals. Are you using it?
                </p>
                
                <div className="space-y-4">
                  {[
                    { icon: Users, text: "Access 900M+ professionals worldwide" },
                    { icon: Target, text: "Laser-target decision-makers by role, company, industry" },
                    { icon: TrendingUp, text: "5x higher response rates than cold email" },
                    { icon: Shield, text: "Build trust through personal connections" },
                    { icon: Clock, text: "Shorter sales cycles with warm introductions" },
                    { icon: BarChart3, text: "Measurable ROI with detailed analytics" },
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#0A66C2]/10 rounded-lg flex items-center justify-center">
                        <item.icon className="w-5 h-5 text-[#0A66C2]" />
                      </div>
                      <p className="text-foreground">{item.text}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-[#0A66C2]/10 to-secondary/10 rounded-2xl p-8 border border-border">
                <h3 className="text-2xl font-bold text-foreground mb-6 text-center">
                  The Question Is Simple
                </h3>
                <div className="space-y-6">
                  <div className="bg-background rounded-xl p-6 text-center">
                    <p className="text-lg text-muted-foreground mb-2">Could your business benefit from</p>
                    <p className="text-4xl font-bold text-[#0A66C2] transition-all duration-300">{animatedCalls}+ Calls Booked</p>
                    <p className="text-lg text-muted-foreground">with qualified prospects?</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-semibold text-foreground mb-2">
                      If yes, why aren't you taking action yet?
                    </p>
                    <p className="text-muted-foreground">
                      Every day you wait is another day your competitors are winning.
                    </p>
                  </div>
                  <Button asChild className="w-full bg-secondary hover:bg-secondary/90 text-primary font-bold" size="lg">
                    <a href="#contact">
                      Book Your Strategy Call Now
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section id="pricing" className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge className="mb-4">Pricing</Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Transparent Pricing, Predictable Results
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Choose the plan that fits your growth goals. All plans include setup, targeting, and messaging.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {pricingTiers.map((tier, idx) => (
                <Card key={idx} className={`relative overflow-hidden ${tier.popular ? 'border-[#0A66C2] border-2 shadow-xl' : 'border-border/50'}`}>
                  {tier.popular && (
                    <div className="absolute top-0 right-0 bg-[#0A66C2] text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                      MOST POPULAR
                    </div>
                  )}
                  <CardHeader className="text-center pb-4">
                    <CardTitle className="text-xl">{tier.name}</CardTitle>
                    <p className="text-muted-foreground text-sm">{tier.description}</p>
                    <div className="mt-4">
                      <span className="text-4xl font-bold text-foreground">{tier.price}</span>
                      <span className="text-muted-foreground">{tier.period}</span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3 mb-6">
                      {tier.features.map((feature, fidx) => (
                        <li key={fidx} className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button 
                      asChild 
                      className={`w-full ${tier.popular ? 'bg-[#0A66C2] hover:bg-[#0A66C2]/90' : ''}`}
                      variant={tier.popular ? "default" : "outline"}
                    >
                      <a href="#contact">{tier.cta}</a>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>

            <p className="text-center text-muted-foreground mt-8">
              All plans require a 3-month minimum commitment. Custom enterprise solutions available.
            </p>
          </div>
        </section>

        {/* Contact Form */}
        <section id="contact" className="py-20 bg-gradient-to-br from-primary via-[#0A66C2] to-[#004182]">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Ready to Transform Your LinkedIn?
                </h2>
                <p className="text-lg text-white/80">
                  Book a free strategy call and we'll show you exactly how we can fill your pipeline with qualified leads.
                </p>
              </div>

              <Card className="border-0 shadow-2xl">
                <CardContent className="p-8">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          Full Name *
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="John Smith"
                          className={errors.name ? "border-destructive" : ""}
                        />
                        {errors.name && <p className="text-destructive text-sm">{errors.name}</p>}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          Work Email *
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="john@company.com"
                          className={errors.email ? "border-destructive" : ""}
                        />
                        {errors.email && <p className="text-destructive text-sm">{errors.email}</p>}
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="company" className="flex items-center gap-2">
                          <Building2 className="w-4 h-4" />
                          Company Name *
                        </Label>
                        <Input
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleInputChange}
                          placeholder="Acme Inc"
                          className={errors.company ? "border-destructive" : ""}
                        />
                        {errors.company && <p className="text-destructive text-sm">{errors.company}</p>}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="flex items-center gap-2">
                          <Phone className="w-4 h-4" />
                          Phone (Optional)
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="+44 7XXX XXXXXX"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Tell us about your goals (Optional)</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="What are you hoping to achieve with LinkedIn outreach? Who is your ideal customer?"
                        rows={4}
                      />
                    </div>

                    <Button 
                      type="submit" 
                      size="lg" 
                      className="w-full bg-[#0A66C2] hover:bg-[#0A66C2]/90 font-bold text-lg"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        "Sending..."
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Get My Free Strategy Call
                        </>
                      )}
                    </Button>

                    <p className="text-center text-muted-foreground text-sm">
                      No spam. No pressure. Just a conversation about your growth goals.
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
              Still Have Questions?
            </h2>
            <p className="text-muted-foreground mb-6">
              Book a call with our team. We'll answer everything and show you exactly what's possible.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg">
                <Link to="/book-call">
                  <Calendar className="w-5 h-5 mr-2" />
                  Book a Call
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/">
                  Back to Home
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default LinkedInSDR;